import styles from './form.module.css'

import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'

import {
  createEmailGroup,
  loadEmailGroups,
  updateEmailGroup,
} from '@disco/state-management/actions/EmailGroupActions'
import { updateBusinessSettings } from '@disco/state-management/actions/UserActions'
import { getEmailGroup } from '@disco/state-management/selectors/emailGroups'

import type { RecipientList } from 'types/list'

import BusinessAddressField from 'components/common/business-address-field'
import HelpTooltip from 'components/common/tooltip'
import { Box, Button, Flex, Spinner, Text, TextField } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import type { MailingListsOutletContext } from 'pages/contacts/mailing-lists'

type DraftJsDoc = Record<string, any>

type FieldErrors = {
  name?: string
  sender_name?: string
  sender_email?: string
  business_address_text?: string
}

const validate = (values: {
  name: string
  sender_name: string
  sender_email: string
  business_address_text: string | null | undefined
}): FieldErrors => {
  const errors: FieldErrors = {}
  if (!values.name.trim()) {
    errors.name = 'Please enter a name'
  } else if (values.name.length > 100) {
    errors.name = 'The name may not be greater than 100 characters'
  }
  if (!values.sender_name.trim()) {
    errors.sender_name = 'Please enter a sender name'
  }
  if (!values.sender_email.trim()) {
    errors.sender_email = 'Please enter a sender address'
  }
  if (!(values.business_address_text || '').trim()) {
    errors.business_address_text = 'Please enter a business address'
  }
  return errors
}

const MailingListForm = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const params = useParams<{ listId?: string }>()
  const { onListCreated } = useOutletContext<MailingListsOutletContext>()
  const listId = params.listId ? parseInt(params.listId) : undefined
  const list = useSelector(state =>
    listId ? getEmailGroup(state, { listId }) : null,
  ) as RecipientList | null | undefined
  const user = useSelector(state => state.user)
  const emailGroups = useSelector(state => state.emailGroups)

  const [name, setName] = useState(list ? list.name : '')
  const [senderName, setSenderName] = useState(
    (list ? list.sender_name : '') || user.first_name,
  )
  const [senderEmail, setSenderEmail] = useState(
    (list ? list.sender_email : '') || user.email,
  )
  const [businessAddress, setBusinessAddress] = useState<
    DraftJsDoc | null | undefined
  >(user.business && user.business.business_address)
  const [businessAddressMjml, setBusinessAddressMjml] = useState<
    string | null | undefined
  >(user.business && user.business.business_address_mjml)
  // '_' - filler, only for validation
  const [businessAddressText, setBusinessAddressText] = useState<
    string | null | undefined
  >('_')
  const [errors, setErrors] = useState<FieldErrors>({})

  useEffect(() => {
    dispatch(loadEmailGroups())
  }, [dispatch])

  // The useState initializers above only run once, so a deep-link or hard
  // refresh on the edit URL - where `list` is still null on first render
  // because groups haven't loaded yet - would otherwise leave the form
  // permanently blank once the fetch resolves. Seed once, the first time
  // `list` becomes available.
  const hasSeededRef = useRef(false)
  useEffect(() => {
    if (!list || hasSeededRef.current) return
    hasSeededRef.current = true
    setName(list.name)
    setSenderName(list.sender_name || user.first_name)
    setSenderEmail(list.sender_email || user.email)
  }, [list, user.first_name, user.email])

  const handleBusinessAddressChange = (val, _valHtml, valMjml, valText) => {
    setErrors({})
    setBusinessAddress(val)
    setBusinessAddressMjml(valMjml)
    setBusinessAddressText(valText)
  }

  const submitToServer = async () => {
    const attrs: Partial<RecipientList> = {
      name,
      sender_name: senderName,
      sender_email: senderEmail,
    }

    if (
      user.permissions?.includes('can_change_business_settings') &&
      user.business?.business_address_mjml !== businessAddressMjml
    ) {
      await dispatch(
        updateBusinessSettings(
          {
            business_address: businessAddress,
            // careful: html (business_address_mjml) is stored as "mjml"
            // generally this is not correct: html has to be wrapped in mjml tag
            business_address_mjml: businessAddressMjml,
          },
          false,
        ),
      )
    }

    if (list) {
      attrs.id = list.id
      attrs.recipients = list.recipients
      await dispatch(updateEmailGroup(attrs))
      navigate(`/lists/${attrs.id}`)
    } else {
      await dispatch(createEmailGroup(attrs))
      onListCreated()
      navigate(`/lists`)
    }
  }

  const handleSubmit = (evt: React.FormEvent) => {
    evt.preventDefault()
    const nextErrors = validate({
      name,
      sender_name: senderName,
      sender_email: senderEmail,
      business_address_text: businessAddressText,
    })
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length === 0) submitToServer()
  }

  const handleClose = () => {
    navigate('/lists')
  }

  if (emailGroups.isFetching) {
    return (
      <Flex justify="center" my="6">
        <Spinner size="3" />
      </Flex>
    )
  }

  const valid = Object.keys(errors).length === 0

  return (
    <form onSubmit={handleSubmit}>
      <Flex direction="column" gap="4" mt="4">
        <Box>
          <Text as="label" size="2" weight="medium" htmlFor="mailing-list-name">
            Mailing list name
            <Text color="red"> *</Text>
          </Text>
          <TextField.Root
            id="mailing-list-name"
            mt="1"
            name="name"
            value={name}
            onChange={evt => {
              setErrors({})
              setName(evt.target.value)
            }}
          />
          {errors.name && (
            <Text size="1" color="red" mt="1" as="div">
              {errors.name}
            </Text>
          )}
        </Box>
        <Box>
          <Flex align="center" gap="1">
            <Text as="label" size="2" weight="medium" htmlFor="sender-name">
              Sender display name
            </Text>
            <HelpTooltip id="tooltip_campaigns_sender_name" />
          </Flex>
          <TextField.Root
            id="sender-name"
            mt="1"
            name="sender_name"
            placeholder={user.first_name}
            value={senderName}
            onChange={evt => {
              setErrors({})
              setSenderName(evt.target.value)
            }}
          />
          {errors.sender_name && (
            <Text size="1" color="red" mt="1" as="div">
              {errors.sender_name}
            </Text>
          )}
        </Box>
        <Box>
          <Flex align="center" gap="1">
            <Text as="label" size="2" weight="medium" htmlFor="sender-email">
              Sender from address
            </Text>
            <HelpTooltip id="tooltip_campaigns_sender_email" />
          </Flex>
          <TextField.Root
            id="sender-email"
            mt="1"
            name="sender_email"
            placeholder={user.email}
            value={senderEmail}
            onChange={evt => {
              setErrors({})
              setSenderEmail(evt.target.value)
            }}
          />
          {errors.sender_email && (
            <Text size="1" color="red" mt="1" as="div">
              {errors.sender_email}
            </Text>
          )}
        </Box>
        <Box>
          <Flex align="center" gap="1">
            <Text as="label" size="2" weight="medium">
              Business address
            </Text>
            <HelpTooltip id="tooltip_campaigns_business_address" />
          </Flex>
          <Box mt="1" className={styles.businessAddress}>
            <BusinessAddressField
              invalid={!!errors.business_address_text}
              initialContent={businessAddress}
              onChange={handleBusinessAddressChange}
            />
          </Box>
          {errors.business_address_text && (
            <Text size="1" color="red" mt="1" as="div">
              {errors.business_address_text}
            </Text>
          )}
        </Box>
      </Flex>

      {emailGroups.error ? (
        <Text size="2" color="red" mt="3" as="div">
          Failed to save
        </Text>
      ) : null}
      <Flex gap="3" mt="4" justify="end">
        <Button
          type="button"
          variant="soft"
          color="gray"
          onClick={handleClose}
          disabled={emailGroups.isSaving}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!valid || emailGroups.isSaving}
          loading={emailGroups.isSaving}
        >
          Save
        </Button>
      </Flex>
    </form>
  )
}

export default MailingListForm
